import { useEffect } from 'react';

/**
 * useSEO – dynamically updates document title, meta tags, and canonical URL.
 *
 * @param {Object} options
 * @param {string} options.title         - Page <title>
 * @param {string} options.description   - Meta description (155–160 chars ideal)
 * @param {string} options.keywords      - Meta keywords (comma-separated)
 * @param {string} [options.canonical]   - Canonical URL (absolute)
 * @param {string} [options.ogImage]     - Open Graph image URL
 * @param {string} [options.ogType]      - OG type (default: "website")
 * @param {Object} [options.structuredData] - JSON-LD object (injected into <head>)
 */
const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://agoinnovations.in/og-image.jpg',
  ogType = 'website',
  structuredData,
}) => {
  useEffect(() => {
    // ── Title ──────────────────────────────────────────────────────────────
    if (title) {
      document.title = title;
    }

    // ── Helper: set or create a <meta> tag ─────────────────────────────────
    const setMeta = (selector, content) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const attr = selector.match(/\[(\w+)="([^"]+)"\]/);
        if (attr) el.setAttribute(attr[1], attr[2]);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // ── Helper: set or create a <link> tag ────────────────────────────────
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // ── Standard meta ─────────────────────────────────────────────────────
    setMeta('meta[name="description"]', description);
    setMeta('meta[name="keywords"]', keywords);

    // ── Open Graph ────────────────────────────────────────────────────────
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:type"]', ogType);
    setMeta('meta[property="og:image"]', ogImage);
    if (canonical) setMeta('meta[property="og:url"]', canonical);

    // ── Twitter ───────────────────────────────────────────────────────────
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', ogImage);

    // ── Canonical ─────────────────────────────────────────────────────────
    setLink('canonical', canonical);

    // ── JSON-LD structured data ────────────────────────────────────────────
    const SCRIPT_ID = 'seo-structured-data';
    let scriptEl = document.getElementById(SCRIPT_ID);
    if (structuredData) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = SCRIPT_ID;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(structuredData);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    // ── Cleanup: restore homepage title on unmount ─────────────────────────
    return () => {
      document.title =
        'AGO Innovations | Assignment Help, Student Services & Finance in India';
    };
  }, [title, description, keywords, canonical, ogImage, ogType, structuredData]);
};

export default useSEO;
